import { Controller } from "@hotwired/stimulus";

// Reveals screener questions one at a time with an animated slide between them,
// ending on a review step that lists every answer before submitting. All questions
// stay in the DOM (single form, submitted at once) — the reveal, auto-advance,
// progress, and review are purely client-side. Single-choice questions auto-advance
// on selection; free-text and multi-select checklists advance via a Continue button.
export default class extends Controller {
  static targets = [
    "question",
    "summary",
    "summaryAnswer",
    "dot",
    "progressBar",
    "submitButton",
    "progressLabel",
  ];
  static values = {
    storageKey: String,
    index: { type: Number, default: 0 },
    positionTemplate: String,
    summaryLabel: String,
  };

  connect() {
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.restore();
    this.indexValue = this.clamp(this.indexValue);
    this.steps.forEach((step, i) => {
      step.classList.toggle("hidden", i !== this.indexValue);
    });
    if (this.onSummary) this.populateSummary();
    this.paint();
  }

  answerChanged() {
    this.persist();
    this.paint();

    if (this.editing) {
      this.editing = false;
      this.scheduleAdvance(this.summaryIndex, "forward");
      return;
    }
    if (this.indexValue >= this.lastIndex) return;
    this.scheduleAdvance(this.indexValue + 1, "forward");
  }

  // Checklists take several picks, so record and repaint without advancing —
  // the student moves on with the Continue button.
  selectionChanged() {
    this.persist();
    this.paint();
  }

  // Advance from a free-text or checklist step (no single option to tap).
  continueStep() {
    this.persist();
    if (this.editing) {
      this.editing = false;
      this.go(this.summaryIndex, "forward");
      return;
    }
    this.next();
  }

  scheduleAdvance(toIndex, direction) {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => this.go(toIndex, direction), 250);
  }

  next() {
    this.editing = false;
    if (this.indexValue < this.lastIndex)
      this.go(this.indexValue + 1, "forward");
  }

  back(event) {
    this.editing = false;
    if (this.indexValue > 0) {
      if (event) event.preventDefault();
      this.go(this.indexValue - 1, "backward");
    }
    // At the first step, let the link fall through to the screeners index.
  }

  editQuestion(event) {
    this.editing = true;
    this.go(Number(event.params.index), "backward");
  }

  go(newIndex, direction) {
    const current = this.steps[this.indexValue];
    const target = this.steps[newIndex];
    this.indexValue = newIndex;
    this.persist();

    if (this.reducedMotion || !current || current === target) {
      if (current) current.classList.add("hidden");
      this.reveal(target, null);
      return;
    }

    const outClass =
      direction === "forward"
        ? "animate__fadeOutLeft"
        : "animate__fadeOutRight";
    const inClass =
      direction === "forward" ? "animate__fadeInRight" : "animate__fadeInLeft";

    this.animate(current, outClass, () => {
      current.classList.add("hidden");
      this.reveal(target, inClass);
    });
  }

  reveal(el, inClass) {
    el.classList.remove("hidden");
    if (this.hasSummaryTarget && el === this.summaryTarget) {
      this.populateSummary();
    }
    if (inClass) this.animate(el, inClass);
    this.paint();
    requestAnimationFrame(() => el.focus({ preventScroll: true }));
  }

  animate(el, name, done) {
    el.classList.add("animate__animated", "animate__faster", name);
    el.addEventListener(
      "animationend",
      () => {
        el.classList.remove("animate__animated", "animate__faster", name);
        if (done) done();
      },
      { once: true },
    );
  }

  populateSummary() {
    this.summaryAnswerTargets.forEach((el) => {
      const card = this.questionTargets[Number(el.dataset.questionIndex)];
      el.textContent = card ? this.summaryFor(card) : "—";
    });
  }

  summaryFor(card) {
    const checked = card.querySelector('input[type="radio"]:checked');
    if (checked) return this.labelFor(checked);

    if (card.querySelector('input[type="checkbox"]')) {
      const labels = [
        ...card.querySelectorAll('input[type="checkbox"]:checked'),
      ].map((box) => this.labelFor(box));
      const writeIn = card.querySelector('input[type="text"]')?.value.trim();
      if (writeIn) labels.push(writeIn);
      return labels.length ? labels.join(", ") : "—";
    }

    const textarea = card.querySelector("textarea");
    if (textarea) return textarea.value.trim() || "—";
    return "—";
  }

  labelFor(input) {
    return input.closest("label")?.querySelector("span")?.textContent.trim();
  }

  paint() {
    this.dotTargets.forEach((dot, i) => {
      const tone =
        i === this.indexValue
          ? "bg-violet-600 scale-110"
          : this.answered(i)
            ? "bg-violet-400"
            : "bg-zinc-200";
      dot.className = `h-2.5 w-2.5 rounded-full transition-all ${tone}`;
    });

    if (this.hasProgressBarTarget) {
      const total = this.questionTargets.length;
      const current = Math.min(this.indexValue, total);
      this.progressBarTarget.style.width = `${
        total ? Math.round((current / total) * 100) : 0
      }%`;
    }

    if (this.hasSubmitButtonTarget) {
      const submitVisible = this.hasSummaryTarget
        ? this.onSummary
        : this.indexValue === this.lastIndex;
      this.submitButtonTarget.classList.toggle("hidden", !submitVisible);
    }

    if (this.hasProgressLabelTarget) {
      if (this.onSummary && this.hasSummaryLabelValue) {
        this.progressLabelTarget.textContent = this.summaryLabelValue;
      } else if (this.hasPositionTemplateValue) {
        this.progressLabelTarget.textContent = this.positionTemplateValue
          .replace(
            "%{current}",
            Math.min(this.indexValue + 1, this.questionTargets.length),
          )
          .replace("%{total}", this.questionTargets.length);
      }
    }
  }

  answered(i) {
    const card = this.questionTargets[i];
    if (!card) return false;
    if (card.querySelector('input[type="radio"]:checked')) return true;
    // Checklists are optional: count them once something is ticked, or passed.
    if (card.querySelector('input[type="checkbox"]'))
      return (
        card.querySelector('input[type="checkbox"]:checked') !== null ||
        i < this.indexValue
      );
    const textarea = card.querySelector("textarea");
    // Free-text is optional: count it once typed in or passed.
    if (textarea)
      return textarea.value.trim().length > 0 || i < this.indexValue;
    return false;
  }

  // Safety net: block submit if a choice question is unanswered and jump to it.
  validate(event) {
    event.preventDefault();
    const form = event.target;
    const names = new Set();
    const answered = new Set();
    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      names.add(radio.name);
      if (radio.checked) answered.add(radio.name);
    });

    if (answered.size < names.size) {
      const firstUnanswered = this.questionTargets.findIndex(
        (q) =>
          q.querySelector('input[type="radio"]') &&
          !q.querySelector('input[type="radio"]:checked'),
      );
      if (firstUnanswered >= 0) {
        const direction =
          firstUnanswered > this.indexValue ? "forward" : "backward";
        this.go(firstUnanswered, direction);
      }
      return;
    }

    this.clearStorage();
    form.submit();
  }

  persist() {
    if (!this.hasStorageKeyValue) return;
    const answers = {};
    this.element
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((radio) => {
        answers[radio.name] = radio.value;
      });
    // Checklists share one name across options, so collect them into an array.
    this.element
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((box) => {
        (answers[box.name] ||= []).push(box.value);
      });
    this.element
      .querySelectorAll('input[type="text"][name]')
      .forEach((input) => {
        answers[input.name] = input.value;
      });
    this.element.querySelectorAll("textarea[name]").forEach((textarea) => {
      answers[textarea.name] = textarea.value;
    });
    try {
      window.localStorage.setItem(
        this.storageKeyValue,
        JSON.stringify({ answers, index: this.indexValue }),
      );
    } catch {
      // Ignore storage failures (private mode, quota).
    }
  }

  restore() {
    if (!this.hasStorageKeyValue) return;
    let data;
    try {
      data = JSON.parse(
        window.localStorage.getItem(this.storageKeyValue) || "{}",
      );
    } catch {
      return;
    }
    Object.entries(data.answers || {}).forEach(([name, value]) => {
      if (Array.isArray(value)) {
        value.forEach((option) => {
          const box = this.element.querySelector(
            `input[type="checkbox"][name="${CSS.escape(
              name,
            )}"][value="${CSS.escape(option)}"]`,
          );
          if (box) box.checked = true;
        });
        return;
      }
      const radio = this.element.querySelector(
        `input[type="radio"][name="${CSS.escape(name)}"][value="${CSS.escape(
          value,
        )}"]`,
      );
      if (radio) {
        radio.checked = true;
        return;
      }
      const input = this.element.querySelector(
        `input[type="text"][name="${CSS.escape(name)}"]`,
      );
      if (input) {
        input.value = value;
        return;
      }
      const textarea = this.element.querySelector(
        `textarea[name="${CSS.escape(name)}"]`,
      );
      if (textarea) textarea.value = value;
    });
    if (Number.isInteger(data.index)) this.indexValue = data.index;
  }

  clearStorage() {
    if (!this.hasStorageKeyValue) return;
    try {
      window.localStorage.removeItem(this.storageKeyValue);
    } catch {
      // Ignore storage failures.
    }
  }

  get steps() {
    return this.hasSummaryTarget
      ? [...this.questionTargets, this.summaryTarget]
      : this.questionTargets;
  }

  get summaryIndex() {
    return this.questionTargets.length;
  }

  get onSummary() {
    return this.hasSummaryTarget && this.indexValue === this.summaryIndex;
  }

  get lastIndex() {
    return this.steps.length - 1;
  }

  clamp(i) {
    return Math.max(0, Math.min(i, this.lastIndex));
  }
}
